import type * as yup from 'yup'
import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import type { FilterButtonType } from 'zaptic-filter-button'
import { createStringEnum } from '../lib/typeHelpers.no-deps'
import { oneOfStringsAttributeSchema } from './helpers/yup'

@customElement('zaptic-filter-button')
export class FilterButton extends LitElement {
    @property()
    label: string

    @property({
        converter: (val) => filterButtonTypeAttributeSchema.validateSync(val),
    })
    type: FilterButtonType

    @property({ attribute: 'is-active', type: String, converter: (val) => val === 'true' })
    isActive: boolean = false

    @property({ attribute: 'more-filters' })
    moreFilters?: number

    @property({ attribute: 'show-arrow', type: String, converter: (val) => val === 'true' })
    showArrow?: boolean = false

    static styles = css`
        :host {
            display: flex;
            flex-direction: row;
            width: 100%;
            height: 35px;
            align-items: center;
        }

        .buttonWrapper {
            display: flex;
            flex-direction: row;
            align-items: center;
            border-radius: 20px;
            padding: 0px 10px;
            color: var(--grey4);
            background-color: var(--darkerCloudGrey);
            border: 1px solid var(--darkerCloudGrey);
            width: 100%;
            height: 100%;
            box-sizing: border-box;
        }
        .buttonWrapper:hover {
            background-color: var(--white);
            border: 1px solid var(--grey5);
            cursor: pointer;
        }

        .active {
            background-color: var(--backgroundPurple);
            border: 1px solid var(--purple);
            color: var(--purple);
        }
        .active:hover {
            background-color: var(--backgroundPurple);
            border: 1px solid var(--purple);
            color: var(--purple);
        }

        .icon,
        .icon-start {
            width: 13.5px;
            height: 16px;
            padding-right: 4.75px;
            color: var(--grey3);
        }

        .icon-end {
            width: 13.5px;
            height: 16px;
            padding-top: 4px;
            padding-left: 4.75px;
        }

        .label {
            font-weight: var(--font-weight-medium);
            font-size: 14px;
            white-space: nowrap;
        }
    `

    private getIconName() {
        switch (this.type) {
            case 'date':
                return 'calendar'
            case 'event':
                return 'task-checkmark'
            case 'member':
                return 'team'
            case 'sort':
                return 'sort-purple'
            case 'workflow':
                return this.isActive ? 'clipboard-purple' : 'clipboard-grey'
            case 'status':
                return this.isActive ? 'status-purple' : 'status-grey'
            case 'unit-subject':
                return this.isActive ? 'unit-cube-purple' : 'unit-cube'
            default:
                return 'sort-purple'
        }
    }

    render() {
        // Apply styles depending on if button is active or not
        const buttonClass = this.isActive ? 'active' : 'inActive'

        // Add '+ n' with the search filter name e.g 'Task + 1'
        const moreFilters = this.moreFilters ? ` + ${this.moreFilters}` : ''

        // Get icon for button type
        const icon = this.getIconName()

        const arrow = this.showArrow
            ? html`<div class="icon-end"><zaptic-icon icon-name="arrow-down"></zaptic-icon></div>`
            : ''

        return html`
            <div class="buttonWrapper ${buttonClass}">
                <div class="icon"><zaptic-icon icon-name=${icon}></zaptic-icon></div>
                <div class="label">${this.label}${moreFilters}</div>
                ${arrow}
            </div>
        `
    }
}

export const filterButtonType = createStringEnum(
    'date',
    'event',
    'member',
    'sort',
    'workflow',
    'status',
    'unit-subject'
)
const filterButtonTypeAttributeSchema = oneOfStringsAttributeSchema(...filterButtonType) as yup.Schema<FilterButtonType>
